import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Chip, Grid } from "@mui/material";
import { ActionMenu, Button, ConfirmationDialog, Dialog, Dropdown, GenericTable, Input, tCell, toastMessage } from "../utils";
import { useOrgs, usePlant, useUserDetail } from "../../services/hooks";
import { buttonClasses } from "../utils/theme";
import { formatDate } from "../../services/functions";
import { useAddEmailTemplateMutation, useDeleteEmailTemplateMutation, useGetEmailTemplatesMutation, useUpdateEmailTemplateMutation } from "../../api/vmsSlice";

// Checkpoint entry alert templates, each with an email part and an SMS part (superAdmin only). Templates are named (unique per organisation) and available
// to all warehouses of the organisation, or to one warehouse only. Each checkpoint entry alert selects one.

const ALL_WAREHOUSES = { label: "All warehouses", value: "" };
const defaultFilter = { pagesize: 10, page_no: 1, orgId: "" };

export default function EmailTemplates() {
	const { isSuperAdmin, userDetail } = useUserDetail();
	const { orgOptions } = useOrgs();
	const [isModal, setIsModal] = useState(false);
	const [templateToDelete, setTemplateToDelete] = useState(null);
	const updateDataRef = useRef(null);
	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize, orgId } = filterRef.current;

	const [getTemplates, { data, isLoading }] = useGetEmailTemplatesMutation();
	const { data: templates = [], count = 0, variables = [], layouts = [] } = data || {};
	const [handleDelete] = useDeleteEmailTemplateMutation();
	const fetchTemplates = () => getTemplates(filterRef.current);

	useEffect(() => {
		if (isSuperAdmin) fetchTemplates();
	}, [isSuperAdmin]);

	const actionHandlers = {
		edit: (index) => {
			updateDataRef.current = templates[index];
			setIsModal(true);
		},
		delete: (index) => setTemplateToDelete(templates[index]),
	};
	const handleDeleteConfirm = async () => {
		if (!templateToDelete) return;
		const { data: { error, message } = {} } = await handleDelete({ id: templateToDelete._id });
		toastMessage(!error, message);
		setTemplateToDelete(null);
		if (!error) fetchTemplates();
	};

	const header = ["S.no", "Name", "Organization", "Warehouse", "Subject", "SMS", "Last Updated", "Action"];
	const rows = templates.map((template, i) => [
		tCell((page_no - 1) * pagesize + i + 1),
		tCell(template.name),
		tCell(template.orgName),
		tCell(template.plantId ? template.plantName : ALL_WAREHOUSES.label),
		tCell(template.subject || "-"),
		tCell(template.smsText && template.smsContentId ? "Yes" : "No"),
		tCell(formatDate(template.modifiedOn || template.createdOn)),
		<ActionMenu id={i} handlers={actionHandlers} />,
	]);

	const onFilterChange = (change) => {
		filterRef.current = { ...filterRef.current, ...change };
		fetchTemplates();
	};
	const onModalClose = () => {
		setIsModal(false);
		updateDataRef.current = null;
	};

	if (userDetail && !isSuperAdmin) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<Grid container spacing="0.5rem" mb={2}>
				<Grid item xl={6} lg={6} md={5} sm={12} xs={12}>
					<h1 className="title">Alert Templates</h1>
				</Grid>
				<Grid item xl={6} lg={6} md={7} sm={12} xs={12}>
					<Grid container spacing="0.5rem" justifyContent={"flex-end"}>
						<Grid item xl={6} lg={6} md={6} sm={6} xs={12}>
							<Dropdown label="Organization" options={orgOptions} value={orgOptions.find((option) => option.value === orgId) || null} onChange={(option) => onFilterChange({ orgId: option?.value || "", page_no: 1 })} />
						</Grid>
						<Grid item xl={4} lg={4} md={4} sm={4} xs={12}>
							<Button onClick={() => setIsModal(true)} text="Add Template" style={buttonClasses.lynkitOrangeEmpty} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<hr className="bgg" />
			<GenericTable
				header={header}
				rows={rows}
				pageCount={count}
				pageNo={page_no}
				limit={pagesize}
				onPageChange={(page) => onFilterChange({ page_no: page + 1 })}
				onPageSizeChange={(e) => onFilterChange({ pagesize: e.target.value, page_no: 1 })}
				isLoading={isLoading}
			/>
			<ConfirmationDialog open={!!templateToDelete} handleClose={() => setTemplateToDelete(null)} handleSubmit={handleDeleteConfirm} title="Delete Alert Template" submitText="Delete">
				Are you sure you want to delete the template "{templateToDelete?.name}"?
			</ConfirmationDialog>
			{isModal && <TemplateForm open={isModal} onClose={onModalClose} updateData={updateDataRef.current} variables={variables} layouts={layouts} orgOptions={orgOptions} onSaved={fetchTemplates} />}
		</div>
	);
}

const TEXT_FIELDS = ["subject", "heading", "body"];
const SECTION_STYLE = { display: "flex", flexDirection: "column", gap: "0.75rem" };
const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;

function TemplateForm({ open, onClose, updateData, variables, layouts, orgOptions, onSaved }) {
	const [formData, setFormData] = useState({
		name: updateData?.name || "",
		orgId: updateData?.orgId || "",
		plantId: updateData?.plantId || "",
		subject: updateData?.subject || "",
		heading: updateData?.heading || "",
		body: updateData?.body || "",
		layoutKey: updateData?.layoutKey || "alertLayout",
		smsContentId: updateData?.smsContentId || "",
		smsText: updateData?.smsText || "",
	});
	// Layouts are htmlTemplate.js functions in VMS; a new one there shows up in this list.
	const layoutOptions = layouts.map((key) => ({ label: key, value: key }));
	// console.log("layouts", layouts, layoutOptions);
	const [error, setError] = useState({});
	// The text field and cursor position a placeholder chip inserts into.
	const cursorRef = useRef({ name: "body", start: null, end: null });

	const { plantOptions, getPlantDetails } = usePlant(null, "emailTemplatePlants");
	const warehouseOptions = [ALL_WAREHOUSES, ...plantOptions];
	useEffect(() => {
		if (formData.orgId) getPlantDetails({ orgId: formData.orgId, pagesize: 1000, page_no: 1, projection: ["plantName"] });
	}, [formData.orgId]);

	const [handleAdd, { isLoading: isAdding }] = useAddEmailTemplateMutation();
	const [handleUpdate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();

	const setField = (name, value) => setFormData((old) => ({ ...old, [name]: value }));
	const handleInput = (e) => setField(e.target.name, e.target.value);

	const insertPlaceholder = (key) => {
		const { name, start, end } = cursorRef.current;
		const text = formData[name] || "";
		const at = start ?? text.length;
		const placeholder = `{{${key}}}`;
		setField(name, text.slice(0, at) + placeholder + text.slice(end ?? at));
		cursorRef.current = { name, start: at + placeholder.length, end: at + placeholder.length };
	};
	const rememberCursor = (name) => (e) => {
		cursorRef.current = { name, start: e.target.selectionStart, end: e.target.selectionEnd };
	};

	const handleSubmit = async () => {
		const errors = {};
		const { name, subject, heading, body, layoutKey, smsContentId, smsText } = formData;
		if (!name.trim()) errors.name = "Name is required";
		if (!formData.orgId) errors.orgId = "Organization is required";
		// A template needs the email part (subject + body), the SMS part (content ID + text), or both; a part that's
		// started must be complete.
		const emailStarted = [subject, heading, body].some((text) => text.trim());
		const smsStarted = [smsContentId, smsText].some((text) => text.trim());
		if (emailStarted || !smsStarted) {
			if (!subject.trim()) errors.subject = "Subject is required for the email part";
			if (!body.trim()) errors.body = "Body is required for the email part";
		}
		if (smsStarted) {
			if (!smsContentId.trim()) errors.smsContentId = "DLT Content ID is required for the SMS part";
			if (!smsText.trim()) errors.smsText = "SMS text is required for the SMS part";
		}
		// Only the placeholders VMS fills in are allowed; anything else would show as blank in the email or SMS.
		const unknownIn = (text) => [...new Set([...text.matchAll(PLACEHOLDER)].map((match) => match[1]))].filter((key) => !variables.includes(key));
		const unknownMessage = (keys) => `Unknown placeholder(s): ${keys.map((key) => `{{${key}}}`).join(", ")}`;
		for (const name of [...TEXT_FIELDS, "smsText"]) {
			const unknown = unknownIn(formData[name]);
			if (unknown.length && !errors[name]) errors[name] = unknownMessage(unknown);
		}
		setError(errors);
		if (Object.keys(errors).length) return;

		const payload = updateData
			? { id: updateData._id, updateData: { name, subject, heading, body, layoutKey, smsContentId, smsText } }
			: { ...formData, plantId: formData.plantId || null, orgName: orgOptions.find((option) => option.value === formData.orgId)?.label };
		const { data: { error: isError, message } = {} } = updateData ? await handleUpdate(payload) : await handleAdd(payload);
		toastMessage(!isError, message);
		if (!isError) {
			onSaved();
			onClose();
		}
	};

	return (
		<Dialog size="md" open={open} handleClose={onClose} handleSubmit={handleSubmit} title={`${updateData ? "Update" : "Add"} Alert Template`} isSubmitDisabled={isAdding || isUpdating}>
			<div className="form-inputs mt-2">
				<Input label="Name" name="name" value={formData.name} onChange={handleInput} placeholder="For example Evening gate entry" required error={error.name} />
				<Dropdown
					label="Organization"
					options={orgOptions}
					value={orgOptions.find((option) => option.value === formData.orgId) || null}
					onChange={(option) => setFormData((old) => ({ ...old, orgId: option?.value || "", plantId: "" }))}
					required
					error={error.orgId}
					disabled={!!updateData}
				/>
				<Dropdown
					label="Warehouse"
					options={warehouseOptions}
					value={warehouseOptions.find((option) => option.value === formData.plantId) || (updateData?.plantId ? { label: updateData.plantName, value: updateData.plantId } : ALL_WAREHOUSES)}
					onChange={(option) => setField("plantId", option?.value || "")}
					disabled={!!updateData || !formData.orgId}
				/>
				<div>
					<small>Placeholders: click to insert into the field you were last typing in (email or SMS).</small>
					<div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.3rem" }}>
						{variables.map((key) => (
							<Chip key={key} label={`{{${key}}}`} size="small" onClick={() => insertPlaceholder(key)} />
						))}
					</div>
				</div>
			</div>

			<div className="p-3 border boder-rad-10p mt-3" style={SECTION_STYLE}>
				<h6 style={{ margin: 0 }}>Email</h6>
				<small>Sent to the alert's Notify Emails (and CC Emails).</small>
				{TEXT_FIELDS.map((name) => (
					<div key={name} onKeyUpCapture={rememberCursor(name)} onMouseUpCapture={rememberCursor(name)} onFocusCapture={rememberCursor(name)}>
						<Input label={{ subject: "Subject", heading: "Heading", body: "Body" }[name]} name={name} value={formData[name]} onChange={handleInput} multiline={name === "body"} error={error[name]} sx={{ width: "100%" }} />
					</div>
				))}
				<Dropdown
					label="Layout"
					options={layoutOptions}
					value={layoutOptions.find((option) => option.value === formData.layoutKey) || { label: formData.layoutKey, value: formData.layoutKey }}
					onChange={(option) => setField("layoutKey", option?.value || "alertLayout")}
					disableClearable={true}
				/>
			</div>

			<div className="p-3 border boder-rad-10p mt-3" style={SECTION_STYLE}>
				<h6 style={{ margin: 0 }}>SMS</h6>
				<small>Sent to the alert's Notify Mobiles. Copy the text exactly as registered on DLT, with one placeholder per {"{#var#}"}; each value is cut to 30 characters.</small>
				<Input label="DLT Content ID" name="smsContentId" value={formData.smsContentId} onChange={handleInput} error={error.smsContentId} sx={{ width: "100%" }} />
				<div onKeyUpCapture={rememberCursor("smsText")} onMouseUpCapture={rememberCursor("smsText")} onFocusCapture={rememberCursor("smsText")}>
					<Input label="SMS Text" name="smsText" value={formData.smsText} onChange={handleInput} multiline error={error.smsText} sx={{ width: "100%" }} />
				</div>
			</div>
		</Dialog>
	);
}
